import type { Editor } from "@tiptap/react";
import type { RichFormattingExtension } from "./Extensions/RichFormatting";
import type { TextAlignment } from "./Extensions/ClassTextAlign";

type AlignAction = `align${Capitalize<TextAlignment>}`;

export type ToolbarAction =
  | "h1"
  | "h2"
  | "h3"
  | "bold"
  | "italic"
  | "highlight"
  | "strike"
  | AlignAction;

export const TOOLBAR_DIVIDER = "divider";

export type ToolbarItem = ToolbarAction | typeof TOOLBAR_DIVIDER;

export const DEFAULT_TOOLBAR_ACTIONS: ToolbarItem[] = ["bold", "italic"];

export interface ToolbarDataItem {
  icon: string;
  label: string;
  shortCutFriendly?: string;
  run: (editor: Editor) => void;
  isActive: (editor: Editor) => boolean;
  /** Opt-in extension the action depends on. */
  extension?: RichFormattingExtension;
}

export const isToolbarAction = (item: ToolbarItem): item is ToolbarAction =>
  item !== TOOLBAR_DIVIDER;

export const getRequiredExtensions = (
  items: ToolbarItem[]
): RichFormattingExtension[] =>
  items.flatMap((item) => {
    const extension = isToolbarAction(item)
      ? toolbarData[item].extension
      : undefined;
    return extension ? [extension] : [];
  });

const createAlignAction = (alignment: TextAlignment): ToolbarDataItem => ({
  icon: `seem-icon-${alignment}`,
  label: `Align ${alignment}`,
  extension: "textAlign",
  run: (editor) => editor.chain().focus().toggleTextAlign(alignment).run(),
  isActive: (editor) => editor.isActive({ textAlign: alignment }),
});

export const toolbarData: Record<ToolbarAction, ToolbarDataItem> = {
  h1: {
    icon: "seem-icon-h1",
    label: "Heading 1",
    shortCutFriendly: "⌘⌥1",
    run: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor) => editor.isActive("heading", { level: 1 }),
  },
  h2: {
    icon: "seem-icon-h2",
    label: "Heading 2",
    shortCutFriendly: "⌘⌥2",
    run: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor) => editor.isActive("heading", { level: 2 }),
  },
  h3: {
    icon: "seem-icon-h3",
    label: "Heading 3",
    shortCutFriendly: "⌘⌥3",
    run: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor) => editor.isActive("heading", { level: 3 }),
  },
  bold: {
    icon: "seem-icon-bold",
    label: "Bold",
    shortCutFriendly: "⌘B",
    run: (editor) => editor.chain().focus().toggleBold().run(),
    isActive: (editor) => editor.isActive("bold"),
  },
  italic: {
    icon: "seem-icon-italic",
    label: "Italic",
    shortCutFriendly: "⌘I",
    run: (editor) => editor.chain().focus().toggleItalic().run(),
    isActive: (editor) => editor.isActive("italic"),
  },
  highlight: {
    icon: "seem-icon-highlight",
    label: "Highlight",
    extension: "highlight",
    shortCutFriendly: "⌘⇧H",
    run: (editor) => editor.chain().focus().toggleHighlight().run(),
    isActive: (editor) => editor.isActive("highlight"),
  },
  strike: {
    icon: "seem-icon-strike",
    label: "Strikethrough",
    extension: "strike",
    shortCutFriendly: "⌘⇧S",
    run: (editor) => editor.chain().focus().toggleStrike().run(),
    isActive: (editor) => editor.isActive("strike"),
  },
  alignLeft: createAlignAction("left"),
  alignCenter: createAlignAction("center"),
  alignRight: createAlignAction("right"),
};
